import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Dialog,
  DialogPopup,
  DialogTitle,
  EditableTitleField,
  EmptyState,
  Icon,
  Kbd,
  KbdGroup,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Tabs,
  TabsList,
  TabsTab,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useGridHistoryHotkeys,
  useIsMobile,
  usePrefersReducedMotion,
  useSnapshotHistory,
} from "@salestrak/ui";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { FormBanner, FormBannerPicker, hasFormBanner } from "#app/components/forms/form-banner";
import { useNoteMediaUpload } from "#app/lib/hooks/use-note-media";
import { BANNER_PRESETS } from "#app/lib/banner-presets";
import {
  discardDraftWithUndo,
  fillBlockedReason,
  publishBlockedReason,
  renderFormMenuItems,
  unpublishWithUndo,
} from "#app/components/forms/form-menu-items";
import { countOf } from "#app/lib/utils/pluralize";
import { FormBuilderDndScope } from "#app/components/forms/form-builder/form-builder-dnd";
import {
  FormCanvas,
  FormCardGhost,
} from "#app/components/forms/form-builder/form-canvas";
import {
  CanvasZoomControls,
  readCanvasAnchor,
  restoreCanvasAnchor,
  revealCanvasItem,
  useCanvasZoomHotkeys,
  type CanvasAnchor,
  type CanvasDensity,
} from "#app/components/forms/form-builder/form-canvas-zoom";
import { FollowUpRowGhost } from "#app/components/forms/form-builder/form-conditions";
import { FormItemPalette } from "#app/components/forms/form-builder/form-item-palette";
import type { CreateFieldDoor } from "#app/components/forms/form-builder/form-answer-link";
import { FormItemSettings } from "#app/components/forms/form-builder/form-item-settings";
import { FormSettingsPage } from "#app/components/forms/form-builder/form-settings-page";
import { FormPreview } from "#app/components/forms/form-builder/form-preview";
import { FormShareDialog } from "#app/components/forms/form-share-dialog";
import {
  FormSettingsPane,
  type FormSettingsChange,
} from "#app/components/forms/form-builder/form-settings-pane";
import { formStatusLabel } from "#app/components/forms/form-status-badge";
import { FormCanvasSkeleton } from "#app/components/forms/form-skeletons";
import {
  useFormCatalogKnown,
  useFormFieldCatalog,
} from "#app/components/forms/use-form-field-catalog";
import { useFormPublish } from "#app/components/forms/use-form-publish";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { useFormFillDialogActions } from "#app/lib/form-fill-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  REDO_SHORTCUT,
  showUndoToast,
  UNDO_SHORTCUT,
} from "#app/lib/undo-shortcut";
import {
  committedFormName,
  editableForm,
  editableShape,
  fieldItemCount,
  duplicateFormItem,
  formOutline,
  hasSomethingToPublish,
  formVisibility,
  hasUnpublishedChanges,
  isCheckboxItem,
  isFieldItem,
  resolveItemField,
  useFormsStore,
  withItemRelinked,
  type Form,
  type FormCondition,
  type FormFieldItem,
  type FormItem,
  type FormShape,
  UNTITLED_FORM,
} from "#app/lib/forms";

function HeaderAction({
  label,
  shortcut,
  ...props
}: {
  label: string;
  shortcut?: readonly string[];
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={<Button aria-label={label} size="icon-xs" variant="ghost" {...props} />}
      />
      <TooltipPopup>
        <Box align="center" direction="row" gap="xs">
          <Text size="xs">{label}</Text>
          {shortcut ? (
            <KbdGroup>
              {shortcut.map((key) => (
                <Kbd key={key}>{key}</Kbd>
              ))}
            </KbdGroup>
          ) : null}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}

function FormTitleCard({
  form,
  renaming,
  onRenamingChange,
  onCommit,
}: {
  form: Pick<Form, "formId" | "name" | "description" | "bannerUrl" | "bannerMediaId" | "items">;
  renaming: boolean;
  onRenamingChange: (renaming: boolean) => void;
  onCommit: (
    patch: Partial<Pick<Form, "name" | "description" | "bannerPreset" | "bannerMediaId">>,
  ) => void | Promise<void>;
}) {
  const selectBanner = (url: string) => {
    const preset = BANNER_PRESETS.find((candidate) => candidate.url === url);
    if (preset) onCommit({ bannerMediaId: null, bannerPreset: preset.id });
  };
  const { uploadNotePhoto } = useNoteMediaUpload();
  const uploadBanner = async (file: File) => {
    const uploaded = await uploadNotePhoto(file);
    if (uploaded) await onCommit({ bannerMediaId: uploaded.photoId, bannerPreset: null });
  };
  const commitName = (next: string) => {
    const name = committedFormName(next, form.name);
    if (name) onCommit({ name });
  };
  const commitDescription = (next: string) => {
    if (next !== (form.description ?? "")) onCommit({ description: next });
  };
  const questionCount = fieldItemCount(form);
  const eyebrow = [
    "Form",
    questionCount === 0 ? null : countOf(questionCount, "question"),
  ]
    .filter(Boolean)
    .join(" · ");
  const bannerAlt = `${form.name || UNTITLED_FORM} banner`;

  return (
    <Box
      background="card"
      border
      direction="column"
      group="tile"
      overflow="clip"
      rounded="lg"
    >
      {hasFormBanner(form) ? (
        <FormBanner alt={bannerAlt} form={form}>
          <Box bottom={8} hoverReveal="tile" position="absolute" revealOnTouch right={8}>
            <FormBannerPicker
              bannerUrl={form.bannerUrl}
              onRemove={() => onCommit({ bannerMediaId: null, bannerPreset: null })}
              onSelect={selectBanner}
              onUpload={uploadBanner}
              trigger={
                <Button size="xs">
                  <Icon name="image" size="xs" />
                  Change image
                </Button>
              }
            />
          </Box>
        </FormBanner>
      ) : null}
      <Box direction="column" gap="xs" padding="xl">
        <Box align="center" direction="row" gap="md" justify="between" minHeight={24}>
          <Text caps size="2xs" variant="faint" weight="medium">
            {eyebrow}
          </Text>
          {hasFormBanner(form) ? null : (
            <Box hoverReveal="tile" revealOnTouch shrink={false}>
              <FormBannerPicker
                onSelect={selectBanner}
                onUpload={uploadBanner}
                trigger={
                  <Button size="xs" variant="ghost">
                    <Icon color="muted" name="image" size="xs" />
                    Add banner image
                  </Button>
                }
              />
            </Box>
          )}
        </Box>
        <EditableTitleField
          aria-label="Form name"
          editing={renaming}
          fill
          flushLeft
          multiline
          onCommit={commitName}
          onEditingChange={onRenamingChange}
          placeholder={UNTITLED_FORM}
          size="xl"
          value={form.name}
          weight="semibold"
        />
        <EditableTitleField
          aria-label="Form description"
          fill
          flushLeft
          maxHeight={160}
          multiline
          newlines
          onCommit={commitDescription}
          placeholder="Add a description..."
          size="sm"
          truncate={false}
          value={form.description ?? ""}
          variant="muted"
          weight="normal"
        />
      </Box>
    </Box>
  );
}

export function FormBuilderDialog({
  formId,
  open,
  onClose,
  onOpenForm,
}: {
  formId: string | null;
  open: boolean;
  onClose: () => void;
  onOpenForm: (formId: string) => void;
}) {
  const { formsById } = useFormsStore(null, { skip: !formId });
  const form = formId ? formsById.get(formId) ?? null : null;
  const canManage = usePermission("forms.manage");

  return (
    <Dialog
      onOpenChange={(next) => (next ? undefined : onClose())}
      open={open && canManage}
      sheetSize="full"
    >
      <DialogPopup
        initialFocus={false}
        showCloseButton={false}
        size="full"
        surface="background"
      >
        <DialogTitle className="sr-only">
          {form ? `Edit ${form.name || UNTITLED_FORM}` : "Form builder"}
        </DialogTitle>
        {form ? (
          <FormBuilderContent
            key={form.formId}
            stored={form}
            onClose={onClose}
            onOpenForm={onOpenForm}
          />
        ) : formId ? (
          <Box align="center" fill="flex" justify="center" padding="xl">
            <EmptyState
              actions={
                <Button onClick={onClose} size="sm" variant="outline">
                  <Text size="sm">Back to Forms</Text>
                </Button>
              }
              description="It may have been deleted."
              icon="clipboard-check"
              media="icon"
              title="Form not found"
            />
          </Box>
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}

const BUILDER_VIEWS = ["build", "preview", "settings"] as const;
type BuilderView = (typeof BUILDER_VIEWS)[number];

function isBuilderView(value: unknown): value is BuilderView {
  return BUILDER_VIEWS.some((view) => view === value);
}

const REVEAL_MARGIN = 24;

function FormBuilderContent({
  stored,
  onClose,
  onOpenForm,
}: {
  stored: Form;
  onClose: () => void;
  onOpenForm: (formId: string) => void;
}) {
  // Every builder surface reads the working copy; `stored` is the published shape fillers see.
  const form = useMemo(() => editableForm(stored), [stored]);
  const store = useFormsStore();

  // Undo walks the working shape and resets on Publish, so undo never stages a rollback of a live form.
  const shape = useMemo(() => editableShape(stored), [stored]);
  const history = useSnapshotHistory<FormShape>({
    value: shape,
    commit: (next) => store.setShape(stored.formId, next),
    isEqual: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    resetKey: `${stored.formId}:${stored.publishedAt ?? ""}`,
  });
  // Typing into one item's settings is one history step, not one per keystroke.
  const editRunRef = useRef<string | null>(null);
  const { snapshot } = history;
  const recordStructure = useCallback(() => {
    editRunRef.current = null;
    snapshot();
  }, [snapshot]);
  const recordItemEdit = useCallback(
    (itemId: string) => {
      if (editRunRef.current === itemId) return;
      editRunRef.current = itemId;
      snapshot();
    },
    [snapshot],
  );
  const onUndo = useCallback(() => {
    editRunRef.current = null;
    history.undo();
  }, [history]);
  const onRedo = useCallback(() => {
    editRunRef.current = null;
    history.redo();
  }, [history]);

  const customFieldsById = useFormFieldCatalog({
    stageWorkflowIds: form.settings?.offeredOn?.workflowIds ?? [],
  });
  // Stand-ins until the workspace fields load: a custom field would read as deleted.
  const catalogKnown = useFormCatalogKnown(form.items);

  const { open: openFill } = useFormFillDialogActions();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const canvasColumnRef = useRef<HTMLDivElement>(null);
  const [sideColumn, setSideColumn] = useState<HTMLDivElement | null>(null);
  const sidePopupOpen = useOpenPopupWithin(sideColumn);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  // The follow-up pane keeps the selection, so the new card is scrolled into view by hand.
  const revealRef = useRef<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  useLayoutEffect(() => {
    const itemId = revealRef.current;
    const scroller = canvasScrollRef.current;
    revealRef.current = null;
    if (itemId && scroller) {
      revealCanvasItem(scroller, itemId, { margin: REVEAL_MARGIN, smooth: !reducedMotion });
    }
  }, [form.items, reducedMotion, selectedItemId]);
  const isMobile = useIsMobile();
  const [chosenView, setView] = useState<BuilderView>("build");
  const view: BuilderView = isMobile && chosenView === "build" ? "preview" : chosenView;
  const preview = view === "preview";
  const shownSelectedId = view === "build" ? selectedItemId : null;
  const [density, setDensity] = useState<CanvasDensity>("full");
  const zoomAnchorRef = useRef<CanvasAnchor | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [sharing, setSharing] = useState(false);

  const canManageFields = usePermission("custom_fields.manage");
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const onCreateField: CreateFieldDoor = canManageFields
    ? (type, onCreated, seed) =>
        openCustomFieldsDialog({ createField: true, onCreated, seed, type })
    : null;

  const selectedItem = useMemo(
    () => form.items.find((item) => item.itemId === selectedItemId) ?? null,
    [form.items, selectedItemId],
  );

  const { requiredByDefault } = form;
  const bornItem = useCallback(
    (item: FormItem): FormItem =>
      requiredByDefault === true && isFieldItem(item) && !isCheckboxItem(item)
        ? { ...item, required: true }
        : item,
    [requiredByDefault],
  );
  const onInsertItem = useCallback(
    (index: number, item: FormItem) => {
      recordStructure();
      const born = bornItem(item);
      revealRef.current = born.itemId;
      store.addItem(form.formId, born, index);
      setSelectedItemId(born.itemId);
      setView("build");
    },
    [bornItem, form.formId, recordStructure, store],
  );
  const onAttachItem = useCallback(
    (itemId: string, index: number, showWhen: FormCondition) => {
      recordStructure();
      revealRef.current = itemId;
      store.moveItem(form.formId, itemId, index, (item) =>
        item.kind === "section" ? item : { ...item, showWhen },
      );
    },
    [form.formId, recordStructure, store],
  );
  const onRuleItems = useCallback(
    (itemIds: readonly string[], showWhen: FormCondition) => {
      if (itemIds.length === 0) return;
      recordStructure();
      for (const itemId of itemIds) {
        store.updateItem(form.formId, itemId, (item) =>
          item.kind === "section" ? item : { ...item, showWhen },
        );
      }
    },
    [form.formId, recordStructure, store],
  );
  // A pick lands under the selected card's whole run, never between the card and its follow-ups.
  const onAddItem = useCallback(
    (item: FormItem) => onInsertItem(form.items.length, item),
    [form.items.length, onInsertItem],
  );
  const onChangeItem = useCallback(
    (patch: (item: FormItem) => FormItem) => {
      if (!selectedItemId) return;
      recordItemEdit(selectedItemId);
      store.updateItem(form.formId, selectedItemId, patch);
    },
    [form.formId, recordItemEdit, selectedItemId, store],
  );
  const relinkSelected = useCallback(
    (relink: (item: FormFieldItem) => FormItem, reason: { one: string; many: string }) => {
      const current = form.items.find((row) => row.itemId === selectedItemId);
      if (!current || !isFieldItem(current)) return;
      const next = relink(current);
      const { unanswered } = withItemRelinked(
        form.items,
        current.itemId,
        next,
        resolveItemField(current, customFieldsById),
        isFieldItem(next) ? resolveItemField(next, customFieldsById) : null,
      );
      recordStructure();
      store.relinkItem(form.formId, current.itemId, relink, customFieldsById);
      const count = unanswered.length;
      if (count === 0) return;
      showUndoToast({
        title:
          count === 1 ? "1 conditional question needs a new answer" : `${count} conditional questions need a new answer`,
        description: count === 1 ? reason.one : reason.many,
        onUndo,
      });
    },
    [customFieldsById, form.formId, form.items, onUndo, recordStructure, selectedItemId, store],
  );
  const onRelinkItem = useCallback(
    (relink: (item: FormFieldItem) => FormItem) =>
      relinkSelected(relink, {
        many: "This link doesn't offer the answers they were shown for, so everyone is asked them until you pick new ones.",
        one: "This link doesn't offer the answer it was shown for, so everyone is asked it until you pick one.",
      }),
    [relinkSelected],
  );
  const onRemoveChoices = useCallback(
    (patch: (item: FormItem) => FormItem) =>
      relinkSelected(patch, {
        many: "The choices they were shown for are gone, so everyone is asked them until you pick new ones.",
        one: "The choice it was shown for is gone, so everyone is asked it until you pick one.",
      }),
    [relinkSelected],
  );
  const onRenameItem = useCallback(
    (itemId: string, label: string) => {
      recordItemEdit(itemId);
      store.updateItem(form.formId, itemId, (item) => ({ ...item, label }));
    },
    [form.formId, recordItemEdit, store],
  );
  const onDuplicateItem = useCallback(
    (itemId: string) => {
      const index = form.items.findIndex((item) => item.itemId === itemId);
      const item = form.items[index];
      if (!item || item.kind === "customField" || item.kind === "property") return;
      const copy = duplicateFormItem(item);
      recordStructure();
      revealRef.current = copy.itemId;
      store.addItem(form.formId, copy, index + 1 + formOutline(form.items).runOf(itemId).length);
      setSelectedItemId(copy.itemId);
    },
    [form.formId, form.items, recordStructure, store],
  );
  const onRemoveCard = useCallback(
    (itemId: string) => {
      recordStructure();
      store.removeItem(form.formId, itemId);
      setSelectedItemId((current) => (current === itemId ? null : current));
    },
    [form.formId, recordStructure, store],
  );
  // Unlike a palette pick, the selection stays on the question that forks.
  const madeAsFollowUps = useRef(new Set<string>());
  const onInsertFollowUp = useCallback(
    (index: number, item: FormItem) => {
      recordStructure();
      madeAsFollowUps.current.add(item.itemId);
      revealRef.current = item.itemId;
      store.addItem(form.formId, bornItem(item), index);
    },
    [bornItem, form.formId, recordStructure, store],
  );
  // The old question leaves the form only if it was made in the Conditions list this session.
  const onReplaceItem = useCallback(
    (itemId: string, next: FormItem) => {
      recordStructure();
      const isNew = !form.items.some((row) => row.itemId === next.itemId);
      if (isNew) madeAsFollowUps.current.add(next.itemId);
      revealRef.current = next.itemId;
      store.replaceItem(
        form.formId,
        itemId,
        isNew ? bornItem(next) : next,
        !madeAsFollowUps.current.has(itemId),
      );
    },
    [bornItem, form.formId, form.items, recordStructure, store],
  );
  const onReleaseItem = useCallback(
    (itemId: string) => {
      recordStructure();
      store.releaseItem(form.formId, itemId);
    },
    [form.formId, recordStructure, store],
  );
  const onRemoveFollowUp = useCallback(
    (itemId: string) =>
      madeAsFollowUps.current.has(itemId) ? onRemoveCard(itemId) : onReleaseItem(itemId),
    [onRemoveCard, onReleaseItem],
  );
  const onReorder = useCallback(
    (activeId: string, overId: string) => {
      // A drag that lands where it started burns no history step.
      if (activeId === overId) return;
      recordStructure();
      store.reorderItems(form.formId, activeId, overId);
    },
    [form.formId, recordStructure, store],
  );
  const onMoveSection = useCallback(
    (sectionItemId: string, by: -1 | 1) => {
      recordStructure();
      store.moveSection(form.formId, sectionItemId, by);
    },
    [form.formId, recordStructure, store],
  );

  // Going Public opens every existing submission, so that one change asks first.
  const [goingPublic, setGoingPublic] = useState<Parameters<FormSettingsChange>[1] | null>(null);
  const onSettingsChange = useCallback<FormSettingsChange>(
    (key, patch) => {
      const current = form.settings ?? {};
      const opens = key === "visibility" && patch(current).visibility === "public" && current.visibility !== "public";
      if (opens && stored.submittedCount > 0) {
        setGoingPublic(() => patch);
        return;
      }
      store.updateSettings(form.formId, patch);
    },
    [form.formId, form.settings, store, stored.submittedCount],
  );
  const onSelectCard = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    setView("build");
  }, []);

  useGridHistoryHotkeys({
    undo: onUndo,
    redo: onRedo,
    enabled: !preview,
  });

  const onZoomChange = useCallback(
    (next: CanvasDensity) => {
      const scroller = canvasScrollRef.current;
      if (scroller) zoomAnchorRef.current = readCanvasAnchor(scroller, selectedItemId);
      setDensity(next);
    },
    [selectedItemId],
  );
  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const scroller = canvasScrollRef.current;
    zoomAnchorRef.current = null;
    if (anchor && scroller) restoreCanvasAnchor(scroller, anchor);
  }, [density]);
  useCanvasZoomHotkeys({ enabled: !preview && !isMobile, density, onChange: onZoomChange });

  // Publishing an empty form would put an empty sheet in every fill picker.
  const publishable = fieldItemCount(form) > 0;
  const hasStaged = hasUnpublishedChanges(stored);
  const publishBlocked =
    publishBlockedReason(stored, store.forms) ?? (hasSomethingToPublish(stored) ? null : "No changes to publish");
  const { publish, dialog: publishDialog } = useFormPublish(store, {
    onInvalidDefaults: ([first]) => {
      if (!first) return;
      setView("build");
      revealRef.current = first;
      setSelectedItemId(first);
    },
    onShare: () => setSharing(true),
  });
  const onPublish = () => publish(stored);

  // Discard and Unpublish skip the confirm and leave an Undo on the toast instead.
  const onDiscard = () => {
    if (discardDraftWithUndo(store, stored)) setSelectedItemId(null);
  };

  const onUnpublish = () => unpublishWithUndo(store, form.formId);

  const onDuplicate = async () => {
    const copy = await store.duplicateForm(form.formId);
    if (copy.ok) onOpenForm(copy.form.formId);
  };
  const onDelete = async () => {
    store.deleteForm(form.formId);
    onClose();
  };

  const statusLabel = formStatusLabel(stored);
  const viewSwitch = (
    <Tabs
      onValueChange={(value) => setView(isBuilderView(value) ? value : "build")}
      value={view}
    >
      <TabsList size="sm">
        {isMobile ? null : <TabsTab value="build">Build</TabsTab>}
        <TabsTab value="preview">Preview</TabsTab>
        <TabsTab value="settings">Settings</TabsTab>
      </TabsList>
    </Tabs>
  );
  const historyActions =
    !preview && !isMobile ? (
      <Box align="center" direction="row" gap="2xs" shrink={false}>
        <HeaderAction
          disabled={!history.canUndo}
          label="Undo"
          onClick={onUndo}
          shortcut={UNDO_SHORTCUT}
        >
          <Icon name="undo" size="md" />
        </HeaderAction>
        <HeaderAction
          disabled={!history.canRedo}
          label="Redo"
          onClick={onRedo}
          shortcut={REDO_SHORTCUT}
        >
          <Icon name="redo" size="md" />
        </HeaderAction>
      </Box>
    ) : null;
  const formActions = (
    <>
      <Tooltip disabled={!publishBlocked}>
        {/* A disabled button takes no pointer, so the reason hangs on a wrapper. */}
        <TooltipTrigger render={<Box />}>
          <Button disabled={!!publishBlocked} onClick={onPublish} size="xs">
            <Text size="xs">Publish</Text>
          </Button>
        </TooltipTrigger>
        <TooltipPopup>{publishBlocked}</TooltipPopup>
      </Tooltip>
      <Menu>
        <MenuTrigger
          render={
            <Button aria-label="Form actions" size="icon-xs" variant="ghost">
              <Icon name="ellipsis" size="md" />
            </Button>
          }
        />
        <MenuPopup align="end">
          {isMobile ? (
            <>
              <MenuItem disabled={preview || !history.canUndo} onClick={onUndo}>
                <Icon color="muted" name="undo" size="md" />
                <Text size="sm">Undo</Text>
              </MenuItem>
              <MenuItem disabled={preview || !history.canRedo} onClick={onRedo}>
                <Icon color="muted" name="redo" size="md" />
                <Text size="sm">Redo</Text>
              </MenuItem>
              <MenuSeparator />
            </>
          ) : null}
          {renderFormMenuItems({
            fillBlocked: fillBlockedReason(stored),
            onDelete: () => setConfirmingDelete(true),
            ...(formVisibility(form) === "private" ? { onShare: () => setSharing(true) } : {}),
            onDuplicate,
            onFill: () => {
              // The picker is mounted at the workspace route, so the builder closes first.
              onClose();
              openFill({
                formId: form.formId,
                formName: form.name,
                kind: "pickIssue",
              });
            },
            ...(hasStaged ? { onDiscardDraft: onDiscard } : {}),
            ...(stored.status === "live" ? { onUnpublish } : {}),
            ...(isMobile
              ? {}
              : {
                  onRename: () => {
                    setView((current) =>
                      current === "preview" || current === "settings" ? "build" : current,
                    );
                    setRenaming(true);
                  },
                }),
          })}
        </MenuPopup>
      </Menu>
      <Button
        aria-label="Close"
        onClick={onClose}
        color="neutral"
        size="icon-xs"
      >
        <Icon name="x" />
      </Button>
    </>
  );
  const settingsProps = {
    form,
    onChange: onSettingsChange,
    onRequiredByDefaultChange: (next: boolean) =>
      store.updateForm(form.formId, { requiredByDefault: next }),
    onShare: () => setSharing(true),
  };

  return (
    <Box direction="column" fill="flex" minHeight={0} overflow="hidden" position="relative">
      {isMobile ? (
        <Box
          align="center"
          border="bottom"
          direction="row"
          gap="sm"
          justify="between"
          minHeight={48}
          paddingX="md"
          shrink={false}
        >
          <Box shrink={false}>{viewSwitch}</Box>
          <Box align="center" direction="row" gap="sm" shrink={false}>
            {formActions}
          </Box>
        </Box>
      ) : (
        <Box
          align="center"
          border="bottom"
          direction="row"
          gap="md"
          minHeight={48}
          paddingX="md"
          shrink={false}
        >
          <Box align="center" direction="row" flex={1} gap="sm" minWidth={0}>
            <Icon color="muted" name="clipboard-check" size="md" />
            <Box align="center" direction="row" gap="sm" minWidth={0}>
              <EditableTitleField
                aria-label="Form name"
                chrome="wash"
                onCommit={(next) => {
                  const name = committedFormName(next, form.name);
                  if (name) store.updateForm(form.formId, { name });
                }}
                placeholder={UNTITLED_FORM}
                size="sm"
                value={form.name}
                variant="muted"
                weight="medium"
              />
              <Box shrink={false}>
                <Text size="sm" variant="faint">
                  · {statusLabel}
                </Text>
              </Box>
            </Box>
          </Box>
          {viewSwitch}
          <Box align="center" direction="row" flex={1} gap="md" justify="end" minWidth={0}>
            {historyActions}
            {formActions}
          </Box>
        </Box>
      )}
      <FormBuilderDndScope
        onInsert={onInsertItem}
        onReorder={onReorder}
        renderCard={(itemId) => (
          <FormCardGhost
            customFieldsById={customFieldsById}
            density={density}
            form={form}
            itemId={itemId}
            selected={itemId === shownSelectedId}
          />
        )}
        renderFollowUp={(itemId) => (
          <FollowUpRowGhost customFieldsById={customFieldsById} itemId={itemId} items={form.items} />
        )}
      >
        <Box direction="row" fill="flex" minHeight={0} position="relative">
          {preview && isMobile && !publishable ? (
            <Box align="center" background="sunken" fill="flex" justify="center" padding="xl">
              <EmptyState
                description="Add questions from a computer or tablet."
                icon="clipboard-check"
                media="icon"
                size="sm"
                title="No questions yet"
              />
            </Box>
          ) : preview ? (
            <FormPreview
              customFieldsById={customFieldsById}
              form={form}
              loading={!catalogKnown}
              status={isMobile ? statusLabel : null}
            />
          ) : view === "settings" && !isMobile ? (
            <FormSettingsPage {...settingsProps} />
          ) : isMobile ? (
            <Box
              background="background"
              direction="column"
              duration={100}
              enter="fade"
              fill="flex"
              minHeight={0}
            >
              <FormSettingsPane {...settingsProps} />
            </Box>
          ) : (
            <Box direction="column" fill="flex" minHeight={0} position="relative">
              <Box
                ref={canvasScrollRef}
                background="sunken"
                direction="column"
                fill="flex"
                minHeight={0}
                onClick={(event) => {
                  if (event.target === event.currentTarget || event.target === canvasColumnRef.current) {
                    setSelectedItemId(null);
                  }
                }}
                overflow="auto"
                // Size container for the column's bottom gutter; its size is flex-determined, so containment is safe.
                className="[container-type:size]"
              >
                <Box
                  ref={canvasColumnRef}
                  direction="column"
                  duration={100}
                  enter="fade"
                  marginX="auto"
                  maxWidth={752}
                  paddingX="2xl"
                  paddingTop="xl"
                  className="pb-[50cqh]"
                  width="100%"
                >
                  <FormTitleCard
                    form={form}
                    onCommit={(patch) => store.updateForm(form.formId, patch)}
                    onRenamingChange={setRenaming}
                    renaming={renaming}
                  />
                  {catalogKnown ? (
                    <FormCanvas
                      customFieldsById={customFieldsById}
                      density={density}
                      form={form}
                      onMoveSection={onMoveSection}
                      onCreateField={onCreateField}
                      onDuplicate={onDuplicateItem}
                      onInsert={onInsertItem}
                      onRemove={onRemoveCard}
                      onRename={onRenameItem}
                      onSelect={onSelectCard}
                      selectedItemId={shownSelectedId}
                    />
                  ) : (
                    <FormCanvasSkeleton count={form.items.length} density={density} />
                  )}
                </Box>
              </Box>
              <Box bottom={16} position="absolute" right={16} zIndex={2}>
                <CanvasZoomControls density={density} onChange={onZoomChange} />
              </Box>
            </Box>
          )}
          {preview || isMobile || view === "settings" ? null : (
            <Box
              ref={setSideColumn}
              border="left"
              direction="column"
              minHeight={0}
              position="relative"
              shrink={false}
              width={320}
            >
              <FormItemPalette form={form} onAddItem={onAddItem} onCreateField={onCreateField} />
              {selectedItem ? (
                <Box
                  background="background"
                  direction="column"
                  enter="from-left"
                  inset={0}
                  minHeight={0}
                  position="absolute"
                >
                  <FormItemSettings
                    customFieldsById={customFieldsById}
                    form={form}
                    item={selectedItem}
                    key={selectedItem.itemId}
                    onClose={() => setSelectedItemId(null)}
                    onChange={onChangeItem}
                    onCreateField={onCreateField}
                    onRelink={onRelinkItem}
                    onRemoveChoices={onRemoveChoices}
                    onAttach={onAttachItem}
                    onDelete={onRemoveFollowUp}
                    onInsert={onInsertFollowUp}
                    onRelease={onReleaseItem}
                    onReplace={onReplaceItem}
                    onRule={onRuleItems}
                    onSelect={setSelectedItemId}
                  />
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </FormBuilderDndScope>
      {sidePopupOpen ? (
        // A clear layer takes the press that closes a side-column popup, so it never reaches the canvas or a card.
        <Box inset={0} position="absolute" zIndex={10} />
      ) : null}
      {publishDialog}
      <AlertDialog
        onOpenChange={(open) => {
          if (!open) setGoingPublic(null);
        }}
        open={goingPublic !== null}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {stored.submittedCount === 1
                ? "Make 1 submission public?"
                : `Make ${stored.submittedCount} submissions public?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Anyone who can see a Project will be able to open this form's submissions on it, including the ones already sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <AlertDialogClose
              onClick={() => {
                if (goingPublic) store.updateSettings(form.formId, goingPublic);
              }}
              render={<Button />}
            >
              Make public
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <FormShareDialog
        form={sharing ? stored : null}
        onChange={(formId, sharedWith) => store.updateForm(formId, { sharedWith })}
        onOpenChange={setSharing}
      />
      <TrashConfirmDialog
        confirmLabel="Delete form"
        description="Its submissions are deleted with it. This cannot be undone."
        onConfirm={onDelete}
        onOpenChange={setConfirmingDelete}
        open={confirmingDelete}
        title={`Delete "${form.name}"?`}
      />
    </Box>
  );
}

/** Base UI stamps `data-popup-open` on the trigger; `aria-expanded` omits a tooltip's, which stamps it too. */
function useOpenPopupWithin(node: HTMLElement | null) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!node) {
      setOpen(false);
      return;
    }
    const read = () =>
      setOpen(node.querySelector('[data-popup-open][aria-expanded="true"]') !== null);
    read();
    const observer = new MutationObserver(read);
    observer.observe(node, {
      attributeFilter: ["data-popup-open", "aria-expanded"],
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [node]);
  return open;
}
